import PropTypes from 'prop-types';
import { useCallback } from 'react';
import Helmet from 'react-helmet';
import { useParams } from 'react-router-dom';

import { isEqual } from 'lodash';
import { DefaultSheet } from 'twyne-new.ui';

import { useAppAlert } from '@Components/AppAlert';
import { useGlobalStore } from '@Stores/global';
import history from 'utils/history';

import formStore from '../../../../offer/stores/form';
import Form from '../components/Form';
import { FieldProvider, useFieldStore } from '../store';

const propTypes = {
  match: PropTypes.object,
}

const FieldForm = ({ isCustomFieldPage = true, cfid, onCancel, onSave }) => {
  const { id } = useParams()
  const { collection: { item, useLoad, useSubmit, reset }, lists: { customFields: { items, useLoad: useLoadCustomFields } } } = useFieldStore()
  const { customFields: { setFormsToSync } } = useGlobalStore()
  const { handleSuccess, handleError } = useAppAlert()

  useLoad(isCustomFieldPage ? id : cfid)
  useLoadCustomFields()
  const handleSubmit = useSubmit()

  const handleClose = useCallback(() => {
    onCancel ? onCancel() : history.push('/path/fields')
    reset()
  }, [onCancel, reset])

  const setFormsByField = useCallback(async customFieldEdited => {
    const currentCustomField = items.find(item => [id, cfid].includes(String(item.id)))
    if (!currentCustomField) return;

    if (!isEqual(currentCustomField.options, customFieldEdited.options) || currentCustomField.type !== customFieldEdited.type) {
      await formStore.all({})
      const formsToSync = formStore.collection.objects.filter(form => {
        const definition = form.definition || []
        for (const step of definition) {
          for (const row of step) {
            for (const col of row.cols) {
              if (col.field === `cf_${customFieldEdited.key}`) return form;
            }
          }
        }
      })
      setFormsToSync([...formsToSync])
    }
  }, [cfid, id, items, setFormsToSync])

  const handleOnSubmit = useCallback(async payload => {
    try {
      if (items.find(option => option.key === payload.key) && !payload.id) {
        handleError('Error', 'Custom Field keys is duplicated.');
        handleClose()
        return;
      }

      if (payload.type === 'select' && (!payload.options || !payload.options.length)) {
        handleError('Error', 'You must add some options.');
        handleClose()
        return;
      }

      await handleSubmit(payload)
      onSave ? onSave() : setFormsByField(payload)
      handleClose()
      handleSuccess('Success', `Custom Field ${payload.id ? 'edited' : 'created'}.`);
    } catch (error) {
      handleError('Error', 'Custom Field was not saved.');
    }
  }, [handleSubmit, handleClose, items, setFormsByField, onSave, handleSuccess, handleError])

  if (!item) {
    return null
  }

  return <>
    <DefaultSheet
      wrapperProps={{ defaultOpen: true, onOpenChange: open => !open && handleClose() }}
      title={item.id ? 'Edit Custom Field' : 'Add Custom Field'}
    >
      <Helmet title="Custom Field" />
      <Form data={item} handleSubmit={handleOnSubmit} onCancel={handleClose} />
    </DefaultSheet>
  </>
};

FieldForm.propTypes = propTypes
FieldForm.defaultProps = {}

export default function FieldFormLayout(props) {
  return <FieldProvider>
    <FieldForm {...props} />
  </FieldProvider>
}
